import React, { useState, memo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Trash2 } from 'lucide-react';
import { deleteEvent, getEventDetails } from '../../services/supabase';
import { withRole } from '../Common/withRole';
import { DangerButton, DefaultButton } from '../Common/BaseButton';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
`;

const ModalContent = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  width: 90%;
  max-width: 400px;
  position: relative;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const ModalTitle = styled.h3`
  margin: 0 0 1rem 0;
  color: #333;
`;

const ModalText = styled.p`
  margin: 0 0 1.5rem 0;
  color: #666;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const MobileIcon = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const DesktopButton = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const DeleteButtonContent = memo(({ eventId, eventTitle, onDelete, isDeleting, onClose, isModalOpen, onOpenModal }) => {
  useEffect(() => {
    if (isModalOpen) {
      // Prevent body scrolling when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scrolling when modal is closed
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to restore scrolling when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  return (
    <>
      <DangerButton onClick={onOpenModal}>
        <DesktopButton>
          <Trash2 size={14} />
          Delete Event
        </DesktopButton>
        <MobileIcon>
          <Trash2 size={16} />
        </MobileIcon>
      </DangerButton>

      {isModalOpen && createPortal(
        <ModalOverlay onClick={onClose}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalTitle>Delete Event</ModalTitle>
            <ModalText>
              Are you sure you want to delete this event and all associated data? This action cannot be undone.
            </ModalText>
            <ButtonGroup>
              <DefaultButton onClick={onClose}>
                Cancel
              </DefaultButton>
              <DangerButton 
                onClick={onDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete Event'}
              </DangerButton>
            </ButtonGroup>
          </ModalContent>
        </ModalOverlay>,
        document.body
      )}
    </>
  );
});

const WithRoleDeleteButton = withRole(DeleteButtonContent, { 
  managerOnly: true
});

const DeleteEventButton = memo(({ eventId, eventTitle, isManager, isAuthenticated }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      const { data: event, error: fetchError } = await getEventDetails(eventId);
      if (fetchError) throw fetchError;
      
      const { error } = await deleteEvent(eventId, event.google_event_id);
      
      if (error) throw error;
      
      navigate('/events');
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Failed to delete event. Please try again.');
    } finally {
      setIsDeleting(false);
      setIsModalOpen(false);
    }
  };

  return (
    <WithRoleDeleteButton 
      isManager={isManager}
      isAuthenticated={isAuthenticated}
      eventId={eventId}
      eventTitle={eventTitle}
      onDelete={handleDelete}
      isDeleting={isDeleting}
      onClose={() => setIsModalOpen(false)}
      onOpenModal={() => setIsModalOpen(true)}
      isModalOpen={isModalOpen}
    />
  );
});

export default DeleteEventButton;